import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add language header
    const language = localStorage.getItem('language') || 'en'
    config.headers['Accept-Language'] = language
    
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          const response = await axios.post('/api/auth/refresh', { refresh_token: refreshToken })
          const { access_token, refresh_token } = response.data.tokens
          
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
}

export const courseAPI = {
  list: (params) => api.get('/courses', { params }),
  get: (id) => api.get(`/courses/${id}`),
  enroll: (id) => api.post(`/courses/${id}/enroll`),
  myEnrollments: (params) => api.get('/courses/my/enrollments', { params }),
}

export const examAPI = {
  start: (moduleId) => api.post(`/exams/modules/${moduleId}/start`),
  getQuestion: (sessionId, index) => api.get(`/exams/sessions/${sessionId}/question/${index}`),
  submitAnswer: (sessionId, data) => api.post(`/exams/sessions/${sessionId}/answer`, data),
  submit: (sessionId) => api.post(`/exams/sessions/${sessionId}/submit`),
  result: (sessionId) => api.get(`/exams/sessions/${sessionId}/result`),
}

export const certificateAPI = {
  list: (params) => api.get('/certificates', { params }),
  get: (id) => api.get(`/certificates/${id}`),
  download: (id) => api.get(`/certificates/${id}/download`, { responseType: 'blob' }),
  myCertificates: () => api.get('/certificates/my'),
  verify: (hash) => api.get(`/certificates/verify/${hash}`),
}

export const officerAPI = {
  dashboard: () => api.get('/officer/dashboard'),
  visits: (params) => api.get('/officer/visits', { params }),
  scheduleVisit: (data) => api.post('/officer/visits', data),
  visitDetail: (id) => api.get(`/officer/visits/${id}`),
  updateVisit: (id, data) => api.put(`/officer/visits/${id}`, data),
  submitReport: (id, data) => api.post(`/officer/visits/${id}/report`, data),
  documents: (params) => api.get('/officer/documents', { params }),
  requestDocument: (data) => api.post('/officer/documents', data),
  reviewDocument: (id, data) => api.put(`/officer/documents/${id}`, data),
  complaints: (params) => api.get('/officer/complaints', { params }),
  updateComplaint: (id, data) => api.put(`/officer/complaints/${id}`, data),
}

export const hrAPI = {
  dashboard: () => api.get('/hr/dashboard'),
  checklist: (params) => api.get('/hr/checklist', { params }),
  updateChecklistItem: (itemId, data) => api.put(`/hr/checklist/${itemId}`, data),
  visits: (params) => api.get('/hr/visits', { params }),
  documents: (params) => api.get('/hr/documents', { params }),
  submitDocument: (data) => api.post('/hr/documents', data),
  complaints: (params) => api.get('/hr/complaints', { params }),
  respondToComplaint: (id, data) => api.put(`/hr/complaints/${id}`, data),
  team: (params) => api.get('/hr/team', { params }),
  calendar: (params) => api.get('/hr/calendar', { params }),
}

export const complianceAPI = {
  calendar: (params) => api.get('/compliance/calendar', { params }),
  checklist: (params) => api.get('/compliance/checklist', { params }),
  updateChecklistItem: (itemId, data) => api.put(`/compliance/checklist/${itemId}`, data),
  auditSimulator: () => api.get('/compliance/audit-simulator'),
  startAudit: (data) => api.post('/compliance/audit-simulator/start', data),
  submitAudit: (id, data) => api.post(`/compliance/audit-simulator/${id}/submit`, data),
}

export const workflowAPI = {
  list: (params) => api.get('/workflows', { params }),
  get: (id) => api.get(`/workflows/${id}`),
  myProgress: () => api.get('/workflows/my/progress'),
  updateProgress: (id, data) => api.put(`/workflows/${id}/progress`, data),
}

export const complaintAPI = {
  list: (params) => api.get('/complaints', { params }),
  create: (data) => api.post('/complaints', data),
  get: (id) => api.get(`/complaints/${id}`),
  update: (id, data) => api.put(`/complaints/${id}`, data),
  addUpdate: (id, data) => api.post(`/complaints/${id}/updates`, data),
}

export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  analytics: (type, month) => api.get('/admin/analytics', { params: { type, month } }),
  
  users: {
    list: (params) => api.get('/admin/users', { params }),
    get: (id) => api.get(`/admin/users/${id}`),
    update: (id, data) => api.put(`/admin/users/${id}`, data),
    delete: (id) => api.delete(`/admin/users/${id}`),
  },
  
  companies: {
    list: (params) => api.get('/admin/companies', { params }),
    get: (id) => api.get(`/admin/companies/${id}`),
    update: (id, data) => api.put(`/admin/companies/${id}`, data),
    delete: (id) => api.delete(`/admin/companies/${id}`),
  },
  
  modules: {
    list: (params) => api.get('/admin/modules', { params }),
    create: (data) => api.post('/admin/modules', data),
    get: (id) => api.get(`/admin/modules/${id}`),
    update: (id, data) => api.put(`/admin/modules/${id}`, data),
    delete: (id) => api.delete(`/admin/modules/${id}`),
  },
  
  scenarios: {
    list: (params) => api.get('/admin/scenarios', { params }),
    create: (data) => api.post('/admin/scenarios', data),
    update: (id, data) => api.put(`/admin/scenarios/${id}`, data),
    delete: (id) => api.delete(`/admin/scenarios/${id}`),
  },
  
  questions: {
    list: (params) => api.get('/admin/questions', { params }),
    create: (data) => api.post('/admin/questions', data),
    update: (id, data) => api.put(`/admin/questions/${id}`, data),
    delete: (id) => api.delete(`/admin/questions/${id}`),
    generate: (data) => api.post('/admin/questions/generate', data),
  },
  
  examTemplates: {
    list: (params) => api.get('/admin/exam-templates', { params }),
    create: (data) => api.post('/admin/exam-templates', data),
    update: (id, data) => api.put(`/admin/exam-templates/${id}`, data),
  },
  
  certificationPaths: {
    list: (params) => api.get('/admin/certification-paths', { params }),
    create: (data) => api.post('/admin/certification-paths', data),
    update: (id, data) => api.put(`/admin/certification-paths/${id}`, data),
  },
  
  certificateTemplates: {
    list: (params) => api.get('/admin/certificate-templates', { params }),
    create: (data) => api.post('/admin/certificate-templates', data),
  },
  
  officers: {
    list: (params) => api.get('/admin/officers', { params }),
    create: (data) => api.post('/admin/officers', data),
    update: (id, data) => api.put(`/admin/officers/${id}`, data),
    delete: (id) => api.delete(`/admin/officers/${id}`),
  },
  
  complaints: {
    list: (params) => api.get('/admin/complaints', { params }),
    update: (id, data) => api.put(`/admin/complaints/${id}`, data),
    assign: (id, officerId) => api.put(`/admin/complaints/${id}/assign`, { officer_id: officerId }),
  },
  
  workflows: {
    list: (params) => api.get('/admin/workflows', { params }),
    create: (data) => api.post('/admin/workflows', data),
    update: (id, data) => api.put(`/admin/workflows/${id}`, data),
    delete: (id) => api.delete(`/admin/workflows/${id}`),
  },
  
  complianceEvents: {
    list: (params) => api.get('/admin/compliance-events', { params }),
    create: (data) => api.post('/admin/compliance-events', data),
    update: (id, data) => api.put(`/admin/compliance-events/${id}`, data),
  },
  
  checklistTemplates: {
    list: (params) => api.get('/admin/checklist-templates', { params }),
    create: (data) => api.post('/admin/checklist-templates', data),
  },
  
  aiProviders: {
    list: (params) => api.get('/admin/ai-providers', { params }),
    create: (data) => api.post('/admin/ai-providers', data),
    update: (id, data) => api.put(`/admin/ai-providers/${id}`, data),
  },
  
  aiPrompts: {
    list: (params) => api.get('/admin/ai-prompts', { params }),
    create: (data) => api.post('/admin/ai-prompts', data),
    update: (id, data) => api.put(`/admin/ai-prompts/${id}`, data),
  },
  
  settings: {
    list: () => api.get('/admin/settings'),
    update: (key, value) => api.put(`/admin/settings/${key}`, { value }),
  },
  
  demo: {
    reset: () => api.post('/admin/demo/reset'),
    logs: () => api.get('/admin/demo/logs'),
  },
}

export const demoAPI = {
  reset: () => api.post('/demo/reset'),
}

export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  changePassword: (data) => api.put('/user/password', data),
  getNotifications: () => api.get('/user/notifications'),
  updateNotifications: (data) => api.put('/user/notifications', data),
  getSessions: () => api.get('/user/sessions'),
  revokeSession: (id) => api.delete(`/user/sessions/${id}`),
  revokeAllSessions: () => api.delete('/user/sessions'),
  getActivityLog: (params) => api.get('/user/activity', { params }),
  exportData: () => api.get('/user/export', { responseType: 'blob' }),
  deleteAccount: (password) => api.delete('/user/account', { data: { password } }),
  enable2FA: () => api.post('/user/2fa/enable'),
  disable2FA: (code) => api.post('/user/2fa/disable', { code }),
  getAPIKeys: () => api.get('/user/api-keys'),
  createAPIKey: (data) => api.post('/user/api-keys', data),
  revokeAPIKey: (id) => api.delete(`/user/api-keys/${id}`),
}

export const publicAPI = {
  verifyCertificate: (hash) => api.get(`/certificates/verify/${hash}`),
  fileComplaint: (data) => api.post('/complaints', data),
  laws: {
    list: (params) => api.get('/laws', { params }),
    get: (id) => api.get(`/laws/${id}`),
  },
  workflows: {
    list: (params) => api.get('/workflows', { params }),
    get: (id) => api.get(`/workflows/${id}`),
  },
}

export default api